import React, { useState } from "react";
import { Link } from "react-router-dom";
import API, { formatError } from "@/api";
import { toast } from "sonner";
import { ArrowLeft, Mail } from "lucide-react";

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await API.post("/auth/forgot-password", { email });
            setSent(true);
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex relative overflow-hidden bg-[#0A0A0F]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {/* Background */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-1/3 -left-1/4 w-[700px] h-[700px] rounded-full opacity-50 mix-blend-screen blur-3xl animate-liquid-1"
                    style={{ background: "radial-gradient(circle, rgba(96,165,250,0.25), rgba(96,165,250,0) 60%)" }} />
                <div className="absolute -bottom-1/3 -right-1/4 w-[800px] h-[800px] rounded-full opacity-60 mix-blend-screen blur-3xl animate-liquid-2"
                    style={{ background: "radial-gradient(circle, rgba(167,139,250,0.20), rgba(167,139,250,0) 65%)" }} />
                <div className="absolute inset-0 opacity-[0.025]" style={{
                    backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                    backgroundSize: "44px 44px"
                }} />
            </div>

            <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 relative z-10">
                <div className="w-full max-w-[420px]">
                    <div className="flex items-center justify-center mb-8">
                        <img src="/logo-tom-v3.png" alt="TOM" className="h-14 w-auto object-contain drop-shadow-[0_0_30px_rgba(96,165,250,0.35)]" />
                    </div>

                    <div className="relative">
                        <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-white/[0.12] to-white/[0.02] opacity-80" />
                        <div className="relative bg-white/[0.03] backdrop-blur-2xl rounded-2xl border border-white/[0.08] p-8 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)]">

                            {sent ? (
                                <div className="text-center py-4">
                                    <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                                        <Mail size={22} className="text-emerald-400" />
                                    </div>
                                    <h2 className="text-[22px] font-bold text-white mb-2 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                        Email trimis
                                    </h2>
                                    <p className="text-sm text-white/50 mb-6 leading-relaxed">
                                        Dacă adresa <span className="text-white/75">{email}</span> există în sistem, vei primi un link de resetare în câteva minute.
                                    </p>
                                    <p className="text-xs text-white/30 mb-6">Verifică și folderul Spam dacă nu primești emailul.</p>
                                    <Link to="/login" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
                                        <ArrowLeft size={14} />
                                        Înapoi la autentificare
                                    </Link>
                                </div>
                            ) : (
                                <>
                                    <h2 className="text-[26px] font-bold text-white mb-1.5 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                        Resetează parola
                                    </h2>
                                    <p className="text-sm text-white/45 mb-7">
                                        Introdu adresa de email și îți trimitem un link de resetare.
                                    </p>

                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div>
                                            <label className="block text-[11px] font-medium text-white/55 mb-1.5 uppercase tracking-[0.12em]">Email</label>
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={e => setEmail(e.target.value)}
                                                placeholder="ana@companie.ro"
                                                className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:bg-white/[0.06] focus:border-white/30 transition-all"
                                                required
                                                autoFocus
                                                autoComplete="email"
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full flex items-center justify-center gap-2 bg-white/[0.08] hover:bg-white/[0.14] text-white py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 mt-2 border border-white/15 hover:border-white/30 shadow-lg shadow-black/30"
                                        >
                                            {loading ? (
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            ) : "Trimite link de resetare"}
                                        </button>
                                    </form>

                                    <div className="mt-6 text-center">
                                        <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-white/45 hover:text-white/75 transition-colors">
                                            <ArrowLeft size={12} />
                                            Înapoi la autentificare
                                        </Link>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
